import { useCallback, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import TagIcon from '../Icons/TagIcon';
import CalenderIcon from '../Icons/CalenderIcon';
import BtnConfirm from '../BtnConfirm';
import ArrowForward from '../Icons/ArrowForward';
import Card from '../Card';
import DiscountTag from '../DiscountTag';
import { setSelectedPackage } from '../../../store/servicePackage/actions';
import eventViewServicePDP from 'ui/lib/gtm/eventViewServicePDP';
import eventVariantService from 'ui/lib/gtm/eventVariantService';
import { removeDuplicates } from '../../../helpers/utils/getDefaultCarAndService';
import { useRouter } from '../../../hooks/useCustomRouter';
import { IServiceContent } from '../TypeCarModal';
import { calculateDiscount } from '../../../helpers/utils';
import { IBreadcrumbs } from '../../molecules/Breadcrumbs';
import eventClickCouponService from '../../../helpers/emarsys/coupon-service/eventClickCouponService';
import eventClickPackageService from '../../../helpers/emarsys/package-service/eventClickPackageService';
import { IRootState } from '../../../store';

interface IPackageCarModalProps {
  serviceType: 'package' | 'coupon';
  closeModal: () => void;
  serviceProductTitle: string;
  serviceContent: IServiceContent;
  bodyTypeName: string;
  feature?: string;
  impressionData?: {
    pageTitle: string;
    breadcrumbItems: IBreadcrumbs[];
  };
}

const PackageCarModal = ({
  serviceType,
  closeModal,
  serviceProductTitle,
  bodyTypeName,
  feature = '',
  impressionData,
}: IPackageCarModalProps) => {
  const router = useRouter();
  const dispatch = useDispatch();

  const { categoryService } = useSelector((state: any) => state.dataGTM || {});
  const { selectedCar, selectedPackage } =
    useSelector((state: IRootState) => state.servicePackage) || {};
  const { userData } = useSelector((state: IRootState) => state.auth) || {};

  const [selectedServiceCode, setSelectedServiceCode] = useState(selectedPackage.code);

  // Define service list (package/coupon) from selectedCar
  const serviceList = useMemo(
    () =>
      serviceType === 'package'
        ? removeDuplicates(selectedCar?.servicePackage)
        : removeDuplicates(selectedCar?.couponServices),
    [selectedCar]
  );

  // Define selected service package/coupon based on what user checked
  const selectedService = useMemo(() => {
    return serviceList.find((service) => service?.code === selectedServiceCode);
  }, [selectedServiceCode, serviceList]);

  const discountPercentage = useCallback(
    (discPrice, actualPrice) => {
      return calculateDiscount(discPrice, actualPrice);
    },
    [selectedPackage, selectedService]
  );

  const sanitizedHtml = (string: string) => {
    const regex = /(<([^>]+)>)/gi;
    return string.replace(regex, '');
  };

  const handleSubmitPackage = () => {
    if (serviceType === 'coupon') {
      // Construct URL
      const { carCategory, productCode } = router.query;
      const vehicleCode = selectedCar?.vehicleCode.toLowerCase();
      const serviceCode = selectedServiceCode.toLowerCase();
      const redirectPath = `/${carCategory}/${productCode}/${serviceCode}?vehicleCode=${vehicleCode}`;
      const selectedPackage = selectedCar?.couponServices?.find(
        (item) => item?.code === selectedServiceCode
      );

      eventClickCouponService(
        dispatch,
        selectedPackage?.name,
        selectedCar,
        impressionData?.pageTitle,
        impressionData?.breadcrumbItems,
        userData?.customerNo,
        'pilih kupon servis',
        'pilih kupon servis'
      );

      // Emit onSubmitPackage event
      closeModal();

      // Push to coupon page
      router.push(redirectPath);
      return;
    }

    const selectedPackage =
      serviceType === 'package'
        ? selectedCar?.servicePackage?.find((item) => item?.code === selectedServiceCode)
        : selectedCar?.couponServices?.find((item) => item?.code === selectedServiceCode);

    if (feature === 'COUPON_SERVICE') {
      eventClickCouponService(
        dispatch,
        selectedPackage?.name,
        selectedCar,
        impressionData?.pageTitle,
        impressionData?.breadcrumbItems,
        userData?.customerNo,
        'pilih kupon servis',
        'pilih kupon servis'
      );
    } else if (feature === 'PACKAGE_SERVICE') {
      eventClickPackageService(
        dispatch,
        selectedPackage?.name,
        selectedCar,
        impressionData?.pageTitle,
        impressionData?.breadcrumbItems,
        userData?.customerNo,
        'pilih paket servis',
        'pilih paket servis'
      );
    }
    dispatch(setSelectedPackage(selectedPackage));
    closeModal();
  };

  return (
    <>
      <div className="relative container">
        {Object.keys(selectedPackage).length > 0 && (
          <div className="container p-2 flex flex-col items-stretch">
            <div className="flex justify-start items-center mb-3">
              <p className="text-base font-bold text-reliableBlack leading-tight">
                <span>
                  {serviceType === 'coupon'
                    ? selectedService?.name
                    : selectedService?.typeOfService}
                </span>
                <span className="font-medium text-reliableBlack80 ml-1">
                  {selectedCar?.vehicleName}
                </span>
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div>
                {/* Only show in service package */}
                {serviceType === 'package' && (
                  <p className="flex items-center gap-1.5 mb-1.5 text-xs text-reliableBlack70">
                    <TagIcon />
                    {sanitizedHtml(selectedService?.description)}
                  </p>
                )}

                <p className="flex items-center gap-1.5 text-xs text-reliableBlack70">
                  <CalenderIcon />
                  <span>
                    Berlaku{' '}
                    {serviceType === 'package'
                      ? selectedService?.yearRange
                      : selectedService?.dayOfService + ' Hari'}
                  </span>
                </p>
              </div>

              <div className="text-right">
                <p className="text-[13px] font-semibold text-supportiveRed">
                  {selectedService?.strikeThroughPrice
                    ? selectedService?.strikeThroughPrice?.formattedValue
                    : selectedService?.price?.formattedValue}
                </p>
                {/* Only show if there is discount price */}
                {selectedService?.strikeThroughPrice && (
                  <div className="flex items-center text-sm">
                    <DiscountTag classNames="px-[5px] py-[1.5px] leading-normal">
                      <span className="text-[10px] font-normal text-reliableBlack">
                        {discountPercentage(
                          selectedService?.price?.value,
                          selectedService?.strikeThroughPrice?.value
                        )}
                      </span>
                    </DiscountTag>
                    <p className="ml-1 text-[10px] line-through text-reliableBlack50">
                      {selectedService?.price?.formattedValue}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <hr className="border-t my-5 px-4 w-full border-dashed border-reliableBlack10" />

      <div className="container pb-40 justify-center items-center grid grid-cols-2 gap-3 overflow-y-auto">
        {serviceList?.map((item, index) => (
          <label
            key={`servicePackages${index}`}
            htmlFor={`servicePackages${index}`}
            className="block cursor-pointer h-auto"
          >
            <input
              id={`servicePackages${index}`}
              type="checkbox"
              className="peer sr-only"
              value={item?.code}
              checked={item?.code === selectedServiceCode}
              onChange={(e) => setSelectedServiceCode(e.target.value)}
            />
            <Card
              className={`h-full px-3 py-2 flex justify-center items-center border rounded bg-[#FFFEFE] border-[#E9E9E9] peer-checked:bg-[#FFE9E9] peer-checked:border-supportiveRed box-border`}
            >
              <p
                className={`text-xs font-medium text-reliableBlack peer-checked:text-supportiveRed`}
              >
                {serviceType === 'coupon' ? item?.name : item?.typeOfService}
              </p>
            </Card>
          </label>
        ))}
      </div>

      <div className="fixed bottom-0 flex justify-end w-full lg:hidden">
        <BtnConfirm
          id="btn-filter-modal"
          block={true}
          className="bg-supportiveRed text-white"
          size="large"
          iconType="icon"
          onClick={() => {
            eventViewServicePDP(
              {
                name: serviceProductTitle?.toLowerCase() || 'not available',
                id: serviceProductTitle?.toLowerCase() || 'not available',
                price: Math.floor(
                  selectedPackage?.strikeThroughPrice?.value || selectedPackage?.price?.value
                ),
                brand: 'toyota',
                category: !bodyTypeName
                  ? 'not available'
                  : bodyTypeName.toLowerCase() === 'commercial'
                    ? 'komersial'
                    : bodyTypeName?.toLowerCase(),
                variant: selectedPackage?.name?.toLowerCase() || 'not available',
                discountedPrice: Math.floor(
                  selectedPackage?.price?.value - selectedPackage?.strikeThroughPrice?.value || 0
                ),
              },
              selectedCar?.vehicleName?.toLowerCase(),
              serviceType,
              categoryService
            );
            eventVariantService(
              categoryService === 'commercial' ? 'komersial' : categoryService,
              serviceProductTitle,
              selectedCar?.vehicleName,
              selectedPackage?.name,
              serviceType
            );
            handleSubmitPackage();
          }}
        >
          <div className="flex justify-end items-center gap-3">
            <div className="flex flex-col text-end py-1.5">
              <p className="text-base font-bold uppercase">Pilih variant</p>
            </div>
            <ArrowForward size={20} color="#FFFFFF" />
          </div>
        </BtnConfirm>
      </div>
    </>
  );
};

export default PackageCarModal;
